// Client-side route guard. This is a UX convenience ONLY — it keeps the console
// shell from flashing for signed-out users and bounces them to /login. It is NOT
// the authorization boundary: every admin_* edge function independently enforces
// `requireAdmin` (verify_jwt + is_admin) server-side. A signed-in non-admin sees
// the shell but every data call returns 403.
import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { ADMIN_FORBIDDEN_EVENT } from "../lib/adminApi";

type AuthState = "loading" | "authed" | "unauthed" | "forbidden";

export default function RequireAdmin({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>("loading");

  useEffect(() => {
    // onAuthStateChange emits INITIAL_SESSION after the OAuth PKCE exchange in
    // the URL is processed, so this avoids the getUser()-returns-null race on the
    // post-login redirect.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setState((prev) =>
        session?.user ? (prev === "forbidden" ? "forbidden" : "authed") : "unauthed",
      );
    });
    return () => subscription.unsubscribe();
  }, []);

  // A 403 from any admin_* call means: signed in, but not an admin (ADM-1). Flip
  // to a dedicated state instead of leaving raw "Forbidden" banners on 12 pages.
  useEffect(() => {
    const onForbidden = () => setState((prev) => (prev === "authed" ? "forbidden" : prev));
    window.addEventListener(ADMIN_FORBIDDEN_EVENT, onForbidden);
    return () => window.removeEventListener(ADMIN_FORBIDDEN_EVENT, onForbidden);
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-900">
        <div className="text-sm text-zinc-500">Loading console…</div>
      </div>
    );
  }
  if (state === "unauthed") {
    return <Navigate to="/login" replace />;
  }
  if (state === "forbidden") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-900 px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <div className="text-lg font-semibold text-zinc-100">Not authorized</div>
          <p className="text-sm text-zinc-400">
            You're signed in, but this account isn't an admin. Sign in with an admin
            account to use the console.
          </p>
          <button
            type="button"
            onClick={signOut}
            className="rounded bg-zinc-100 px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-white"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
